import React, { useState, useEffect } from 'react'
import { Switch } from '@headlessui/react'
import { CheckCircleIcon, XCircleIcon, ExclamationIcon } from '@heroicons/react/solid'
import { debounce } from 'throttle-debounce'
import PropTypes from 'prop-types'
import classNames from '@/utils/classNamesLocal'

import { useForm } from 'react-hook-form'
import useQuery from '@/hooks/useQuery'
import { useGlobalState } from '@/state'
import PrimaryButton from '@/shared/Buttons/Primary'
import Card from '@/shared/Card'

// Postmark delivery streams. Which emails go out is decided in the Notifications
// section above; these are the pipes they travel through. Flip a pipe off and
// nothing in it is sent, whatever the switches above say.
const STREAMS = [
  {
    key: 'activity_mailer',
    name: 'Activity & Notifications',
    description: 'Every switch in the Notifications section rides this one: comments, auditions, casting, messages, recommendations, saved search alerts.'
  },
  {
    key: 'personal_mailer',
    name: 'Newsletters & Personal Updates',
    description: 'Monthly newsletters, paid project roundups, and the occasional note from Buford.'
  },
  {
    key: 'drip_mailer',
    name: 'Onboarding & Account',
    description: 'Welcome emails, password resets, and account verification.'
  },
  {
    key: 'payment_mailer',
    name: 'Billing & Payments',
    description: 'Renewals, failed payments, and cards about to expire.'
  },
  {
    key: 'marketing_mailer_education',
    name: 'Courses & Education',
    description: 'Course announcements and class reminders.'
  }
]

const PERMANENT_REASONS = ['SpamComplaint']
const formatReason = (entry) => {
  if (!entry) { return null }
  const reason = Object.keys(entry)[0]
  return { reason, ts: entry[reason] }
}

const ToggleRow = ({ label, checked, onChange, disabled, dimmedLabel }) => (
  <Switch.Group as="div" className="flex items-center justify-between mb-2">
    <Switch.Label as="span" className="flex-grow flex flex-col">
      <span className={classNames('text-sm', dimmedLabel ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300')}>
        {label}
      </span>
    </Switch.Label>
    <Switch
      checked={!!checked}
      onChange={() => { if (!disabled) { onChange() } }}
      className={classNames(
        checked ? 'bg-cccpurple' : 'bg-gray-300 dark:bg-gray-500',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cccpurple'
      )}
    >
      <span className="sr-only" hidden>{label}</span>
      <span aria-hidden="true" className={classNames(
        checked ? 'translate-x-5' : 'translate-x-0',
        'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
      )} />
    </Switch>
  </Switch.Group>
)

ToggleRow.propTypes = {
  label: PropTypes.node.isRequired,
  checked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  dimmedLabel: PropTypes.bool
}

const StreamHeader = ({ stream, unsubEntry, onToggleSubscription, busy }) => {
  const formatted = formatReason(unsubEntry)
  const isUnsubscribed = !!formatted
  const isPermanent = formatted && PERMANENT_REASONS.includes(formatted.reason)
  const switchDisabled = isPermanent || busy

  return (
    <div className='flex items-start gap-3'>
      <div className='flex-shrink-0 mt-0.5'>
        {isUnsubscribed
          ? (isPermanent ? <ExclamationIcon className='h-6 w-6 text-red-500' /> : <XCircleIcon className='h-6 w-6 text-gray-400' />)
          : <CheckCircleIcon className='h-6 w-6 text-green-500' />}
      </div>
      <div className='flex-1 min-w-0'>
        <div className='flex flex-wrap items-baseline gap-x-2'>
          <h4 className='text-base font-semibold text-gray-900 dark:text-gray-100'>{stream.name}</h4>
          {isUnsubscribed
            ? <span className={classNames('text-xs font-medium', isPermanent ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400')}>
                {isPermanent ? `Blocked (${formatted.reason})` : `Unsubscribed`}
              </span>
            : <span className='text-xs font-medium text-green-600 dark:text-green-400'>Subscribed</span>}
        </div>
        <p className='text-sm text-gray-600 dark:text-gray-300 mt-0.5'>{stream.description}</p>
        {isUnsubscribed && isPermanent && (
          <p className='text-xs text-red-600 dark:text-red-400 mt-1'>
            Permanently blocked after a spam complaint. If that wasn't you, tell support and I'll sort it.
          </p>
        )}
        {isUnsubscribed && !isPermanent && (
          <p className='text-xs text-gray-500 dark:text-gray-400 mt-1'>Last action: {formatted.ts}</p>
        )}
      </div>
      <div className='flex-shrink-0 flex flex-col items-end'>
        <Switch.Group as="div" className="flex items-center">
          <Switch.Label as="span" className="sr-only" hidden>
            {isUnsubscribed ? `Resubscribe to ${stream.name}` : `Unsubscribe from ${stream.name}`}
          </Switch.Label>
          <Switch
            checked={!isUnsubscribed}
            disabled={switchDisabled}
            onChange={() => { if (!switchDisabled) { onToggleSubscription(stream.key, isUnsubscribed) } }}
            className={classNames(
              !isUnsubscribed ? 'bg-cccpurple' : 'bg-gray-300 dark:bg-gray-500',
              switchDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
              'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cccpurple'
            )}
          >
            <span aria-hidden="true" className={classNames(
              !isUnsubscribed ? 'translate-x-5' : 'translate-x-0',
              'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
            )} />
          </Switch>
        </Switch.Group>
        {busy && <span className='text-xs text-gray-400 mt-1'>Saving…</span>}
      </div>
    </div>
  )
}

StreamHeader.propTypes = {
  stream: PropTypes.object.isRequired,
  unsubEntry: PropTypes.object,
  onToggleSubscription: PropTypes.func.isRequired,
  busy: PropTypes.bool
}

export const EmailPreferencesForm = ({ preferences, onUpdate }) => {
  const { putpostRequest } = useQuery()
  const [, setToast] = useGlobalState('toast')
  const [streamBusyKey, setStreamBusyKey] = useState(null)
  const [savingAll, setSavingAll] = useState(false)
  const unsubGroups = preferences.unsub_groups || {}
  const unsubAll = preferences.unsubscribe_all

  const toggleUnsubscribeAll = () => {
    setSavingAll(true)
    putpostRequest(`/api/v3/preferences/${preferences.id}`, 'PATCH', { preference: { unsubscribe_all: !unsubAll } }, (err, jsonData) => {
      setSavingAll(false)
      if (err) { return }
      if (onUpdate && jsonData?.preferences) { onUpdate(jsonData.preferences) }
      setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
        <p className="text-sm font-medium dark:text-green-300 text-green-600">{unsubAll ? 'Emails are back on' : 'All emails off'}</p>
        <p className="mt-1 text-sm dark:text-green-300 text-green-400">{unsubAll ? 'Welcome back to the inbox.' : "I'll go quiet. Even about your password."}</p>
      </div>)
    })
  }

  const toggleStreamSubscription = (streamKey, currentlyUnsubscribed) => {
    const endpoint = currentlyUnsubscribed ? 'resubscribe' : 'unsubscribe_stream'
    setStreamBusyKey(streamKey)
    putpostRequest(`/api/v3/preferences/${preferences.id}/${endpoint}`, 'POST', { stream: streamKey }, (err, jsonData) => {
      setStreamBusyKey(null)
      if (err) { return }
      if (onUpdate && jsonData?.preferences) { onUpdate(jsonData.preferences) }
      setToast(<div className='ml-3 w-0 flex-1 pt-0.5'>
        <p className='text-sm font-medium dark:text-green-300 text-green-600'>
          {currentlyUnsubscribed ? 'Resubscribed' : 'Unsubscribed'}
        </p>
        <p className='mt-1 text-sm dark:text-green-300 text-green-400'>
          {currentlyUnsubscribed
            ? 'Talking to the email gods. Emails should resume within a minute.'
            : "Nothing from this group will reach you. Not a peep."}
        </p>
      </div>)
    })
  }

  return <>
    <div className="md:col-span-1 mb-3">
      <div className="px-0 sm:px-4">
        <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100">Email delivery</h3>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          The pipes your emails travel through. Turn one off (or hit unsubscribe in any of my emails) and nothing in it goes out, whatever you picked above.
        </p>
      </div>
    </div>
    <Card title='Email'>
      <div className='space-y-3'>
        {/* Master kill switch */}
        <div className='p-3 rounded-md bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800'>
          <ToggleRow
            label={<span className='font-semibold'>Unsubscribe from all emails</span>}
            checked={unsubAll}
            disabled={savingAll}
            onChange={toggleUnsubscribeAll}
          />
          {unsubAll && (
            <div className='mt-2 p-2 rounded bg-red-100 dark:bg-red-900/40 border border-red-300 dark:border-red-700'>
              <p className='text-sm font-semibold text-red-800 dark:text-red-200'>This blocks ALL emails, including:</p>
              <ul className='text-xs text-red-700 dark:text-red-300 mt-1 list-disc list-inside space-y-0.5'>
                <li>Password reset emails</li>
                <li>Email verification</li>
                <li>Billing and payment notifications</li>
                <li>Casting decisions (shortlisted, cast, dismissed)</li>
              </ul>
              <p className='text-xs font-semibold text-red-800 dark:text-red-200 mt-2'>Lose your password with this on and I can't help you get back in.</p>
            </div>
          )}
        </div>

        {/* Per-stream cards */}
        {STREAMS.map((stream) => (
          <div key={stream.key} className='p-3 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'>
            <StreamHeader
              stream={stream}
              unsubEntry={unsubGroups[stream.key]}
              onToggleSubscription={toggleStreamSubscription}
              busy={streamBusyKey === stream.key}
            />
          </div>
        ))}
      </div>
    </Card>
  </>
}

EmailPreferencesForm.propTypes = {
  preferences: PropTypes.object.isRequired,
  onUpdate: PropTypes.func
}

// Mirrors Preference::UI_DEFAULTS server-side. Keep in sync.
const UI_DEFAULTS = {
  streaks_visible: true,
  name_gradient: null,
  name_glow: false
}

const DEFAULT_GRADIENT_FROM = '#f7784e' // blue-500
const DEFAULT_GRADIENT_TO = '#3499e0'   // purple-500

export const GeneralPreferencesForm = ({ preferences }) => {
  const { putpostRequest } = useQuery()
  const [currentUser, setCurrentUser] = useGlobalState('currentUser')
  const [, setToast] = useGlobalState('toast')
  const [uiPrefs, setUiPrefs] = useState(preferences.ui_preferences || {})
  const streaksVisible = uiPrefs.streaks_visible ?? UI_DEFAULTS.streaks_visible
  const nameGlow = uiPrefs.name_glow ?? UI_DEFAULTS.name_glow
  const { formState, reset, register, unregister, watch, handleSubmit, setValue, getValues } = useForm({
    defaultValues: {
      allow_nsfw: preferences.allow_nsfw,
      enabled_videos: preferences.enabled_videos,
      direct_messages: preferences.direct_messages,
      show_rejected: preferences.show_rejected
    }
  })

  const { isDirty } = formState
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    register('allow_nsfw', { required: false })
    register('enabled_videos', { required: false })
    register('show_rejected', { required: false })
    return () => {
      unregister('allow_nsfw')
      unregister('enabled_videos')
      unregister('show_rejected')
    }
  }, [register])

  const onSubmit = debounce(300, (data) => {
    setLoading(true)
    putpostRequest(`/api/v3/preferences/${preferences.id}`, 'PATCH', { preference: data }, (err, _) => {
      setLoading(false)
      if (err) { return }

      setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
        <p className="text-sm font-medium dark:text-green-300 text-green-600">General Preferences Saved</p>
        <p className="mt-1 text-sm dark:text-green-300 text-green-400">Wait, no hold on. Are they? ....  Yeah just checked, they're saved.</p>
      </div>)

      setCurrentUser({ ...currentUser, showRejectedSubmissions: getValues().show_rejected })
      reset({ ...getValues() })
    })
  })

  const toggle = (key) => setValue(key, !getValues()[key], { shouldDirty: true, shouldValidate: true })
  const allowNsfw = watch('allow_nsfw')
  const enabledVideos = watch('enabled_videos')
  const showRejected = watch('show_rejected')

  const toggleUiPref = (key) => {
    const next = !(uiPrefs[key] ?? UI_DEFAULTS[key])
    putpostRequest('/api/v3/preferences/update_ui', 'POST', { key, value: next }, (err, jsonData) => {
      if (err) { return }
      const updated = jsonData.ui_preferences || {}
      setUiPrefs(updated)
      if (key === 'streaks_visible') {
        setCurrentUser({ ...currentUser, streaksVisible: updated.streaks_visible ?? UI_DEFAULTS.streaks_visible })
      }
    })
  }

  // Username-gradient picker. Stored as { from, to } in ui_preferences.
  const savedGradient = uiPrefs.name_gradient || null
  const [gradientFrom, setGradientFrom] = useState(savedGradient?.from || DEFAULT_GRADIENT_FROM)
  const [gradientTo, setGradientTo] = useState(savedGradient?.to || DEFAULT_GRADIENT_TO)
  const [savingGradient, setSavingGradient] = useState(false)
  const gradientDirty = !savedGradient ||
    savedGradient.from !== gradientFrom ||
    savedGradient.to !== gradientTo

  const saveGradient = () => {
    setSavingGradient(true)
    const value = { from: gradientFrom, to: gradientTo }
    putpostRequest('/api/v3/preferences/update_ui', 'POST', { key: 'name_gradient', value }, (err, jsonData) => {
      setSavingGradient(false)
      if (err) return
      setUiPrefs(jsonData.ui_preferences || {})
      setToast(<div className="ml-3 w-0 flex-1 pt-0.5">
        <p className="text-sm font-medium dark:text-green-300 text-green-600">Gradient saved</p>
      </div>)
    })
  }

  const clearGradient = () => {
    setSavingGradient(true)
    putpostRequest('/api/v3/preferences/update_ui', 'POST', { key: 'name_gradient', value: null }, (err, jsonData) => {
      setSavingGradient(false)
      if (err) return
      setUiPrefs(jsonData.ui_preferences || {})
      setGradientFrom(DEFAULT_GRADIENT_FROM)
      setGradientTo(DEFAULT_GRADIENT_TO)
    })
  }

  return <>
    <div className="md:col-span-1 mb-3">
      <div className="px-0 sm:px-4">
        <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100">General Preferences</h3>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Display and content preferences. Not related to email.</p>
      </div>
    </div>
    <Card title='Preferences' footer={
      <div className='flex justify-end'>
        <PrimaryButton onClick={handleSubmit(onSubmit)} disabled={!isDirty} loading={loading} text='Save' />
      </div>
    }>
      <form className='col-span-3'>
        <div className='p-2 rounded-sm bg-white dark:bg-gray-700 space-y-2'>
          <ToggleRow label='View Submission Videos if there is a video attached' checked={enabledVideos} onChange={() => toggle('enabled_videos')} />
          <ToggleRow label='View NSFW content (I am 18+)' checked={allowNsfw} onChange={() => toggle('allow_nsfw')} />
          <ToggleRow label='Show "Dismissed" label on submissions that were not chosen' checked={showRejected} onChange={() => toggle('show_rejected')} />
          <div className='flex justify-between items-center mb-2'>
            <span className="text-sm text-gray-700 dark:text-gray-300">Who can send me direct messages?</span>
            <select
              {...register('direct_messages')}
              className="block py-2 px-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 dark:text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-cccpurple focus:border-cccpurple sm:text-sm"
            >
              <option value='allow_all'>Everyone</option>
              <option value='private'>Only People I Follow</option>
            </select>
          </div>
          <ToggleRow label='Show audition streak counter on my dashboard' checked={streaksVisible} onChange={() => toggleUiPref('streaks_visible')} />

          <div className='pt-3 border-t border-gray-200 dark:border-gray-600'>
            <div className='text-sm text-gray-700 dark:text-gray-300 mb-2'>
              Username gradient
              <p className='text-xs text-gray-500 dark:text-gray-400 font-normal mt-0.5'>
                Animated gradient applied to your display name when other people hover over a row showing you. Hidden by default.
              </p>
            </div>
            <div className='flex flex-wrap items-end gap-3'>
              <label className='flex flex-col text-xs text-gray-500 dark:text-gray-400'>
                From
                <input
                  type='color'
                  value={gradientFrom}
                  onChange={(e) => setGradientFrom(e.target.value)}
                  className='h-9 w-14 mt-1 rounded cursor-pointer'
                />
              </label>
              <label className='flex flex-col text-xs text-gray-500 dark:text-gray-400'>
                To
                <input
                  type='color'
                  value={gradientTo}
                  onChange={(e) => setGradientTo(e.target.value)}
                  className='h-9 w-14 mt-1 rounded cursor-pointer'
                />
              </label>
              <div className={`flex-1 min-w-[12rem] row-with-gradient-name${nameGlow ? ' row-with-glow-name' : ''} px-3 py-2 rounded border border-gray-200 dark:border-gray-600`}>
                <span className='text-xs text-gray-500 dark:text-gray-400 mr-2'>Hover preview →</span>
                <span
                  className='font-medium text-cccblue gradient-name-target'
                  style={{ '--username-from': gradientFrom, '--username-to': gradientTo }}
                >
                  {currentUser?.displayName || currentUser?.username || 'Your name'}
                </span>
              </div>
            </div>
            <div className='flex items-center gap-3 mt-2'>
              <button
                type='button'
                onClick={saveGradient}
                disabled={savingGradient || !gradientDirty}
                className='px-3 py-1.5 text-xs font-medium rounded-lg text-white bg-cccblue hover:bg-cccblue-alt disabled:opacity-50'
              >
                {savingGradient ? 'Saving…' : (savedGradient ? 'Update gradient' : 'Save gradient')}
              </button>
              {savedGradient && (
                <button
                  type='button'
                  onClick={clearGradient}
                  disabled={savingGradient}
                  className='text-xs text-gray-500 dark:text-gray-400 underline hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-50'
                >
                  Clear gradient
                </button>
              )}
            </div>
            <div className='mt-3'>
              <ToggleRow
                label='Add a soft glow on hover (uses gradient color if set, otherwise purple)'
                checked={nameGlow}
                onChange={() => toggleUiPref('name_glow')}
              />
            </div>
          </div>
        </div>
      </form>
    </Card>
  </>
}

GeneralPreferencesForm.propTypes = {
  preferences: PropTypes.object.isRequired
}

